// src/components/UserChip.tsx
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import FollowButton from "@/components/follow/FollowButton";
import { getAvatarUrlForProfile } from "@/lib/avatars";

type Size = "xs" | "sm" | "md" | "lg";

export type UserChipProps = {
  uid: string;
  username?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  isLGS?: boolean;                  // optional LGS flag
  roles?: { lgs?: boolean };        // optional roles bit
  size?: Size;
  className?: string;
  onViewProfile?: () => void;
  onSendMessage?: () => void;
  onFollow?: () => void;            // (not wired to FollowButton events, but kept for API parity)
};

const AVATAR_SIZES: Record<Size, string> = {
  xs: "w-7 h-7 text-[14px]",
  sm: "w-9 h-9 text-[16px]",
  md: "w-13 h-13 text-[18px]",
  lg: "w-15 h-15 text-[20px]",
};

const PAD_SIZES: Record<Size, string> = {
  xs: "px-1 py-0.5",
  sm: "px-1.5 py-1",
  md: "px-2 py-1.5",
  lg: "px-2.5 py-2",
};

const TEXT_SIZES: Record<Size, string> = {
  xs: "text-[11px]",
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
};

export default function UserChip({
  uid,
  username,
  displayName,
  avatarUrl,
  isLGS,
  roles,
  size = "sm",
  className = "",
  onViewProfile,
  onSendMessage,
  onFollow, // reserved for callers that want to hook into follow actions elsewhere
}: UserChipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const name = username || displayName || "";
  const initials = (name || "U").slice(0, 2).toUpperCase();

  // Size presets (kept close to your existing pattern)
  const sizeClasses =
    size === "sm"
      ? {
          chip: "h-8 text-sm",
          avatar: "h-5 w-5 text-[10px]",
          chevron: "h-3.5 w-3.5",
          name: "text-xs",
        }
      : {
          chip: "h-9 text-sm",
          avatar: "h-6 w-6 text-[11px]",
          chevron: "h-4 w-4",
          name: "text-sm",
        };

  // Build a profile-ish object from props and resolve avatar via central helper.
  const user = {
    avatar_url: avatarUrl ?? null,
    avatar: avatarUrl ?? null,    // tolerate older shapes
    avatarUrl: avatarUrl ?? null, // tolerate alternate key
    isLGS: !!isLGS,
    roles: roles,
  };

  const avatarSrc =
    getAvatarUrlForProfile(user) ??
    // final generic fallback if helper returned null
    (isLGS || roles?.lgs ? "/images/lgs_default.png" : "/images/avatars/default.png");

  const atHandle = username ? `@${username}` : `@${uid}`;

  return (
    <div ref={ref} className={["relative inline-block", className || ""].join(" ")}>
      <button
        type="button"
        className={[
          "group inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-2 pr-1 text-white hover:bg-white/10 transition",
          "shadow-sm",
          sizeClasses.chip,
        ].join(" ")}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={[
            "grid place-items-center rounded-full bg-white/10 text-white/80 overflow-hidden",
            sizeClasses.avatar,
          ].join(" ")}
        >
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={displayName || username || "avatar"}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <span className="font-semibold">{initials}</span>
          )}
        </span>

        <span className={["truncate max-w-[40vw] md:max-w-[240px]", sizeClasses.name].join(" ")}>
          {atHandle}
        </span>

        <ChevronDown
          className={["text-white/70 transition-transform", open ? "rotate-180" : "rotate-0", sizeClasses.chevron].join(
            " ",
          )}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-52 overflow-hidden rounded-md border border-white/10 bg-gray-900 shadow-xl z-50"
        >
          <button
            type="button"
            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10"
            onClick={() => {
              setOpen(false);
              onViewProfile?.();
            }}
          >
            View profile
          </button>

          <button
            type="button"
            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10"
            onClick={() => {
              setOpen(false);
              onSendMessage?.();
            }}
          >
            Send message
          </button>

          <div className="px-3 py-2">
            <FollowButton
              targetUid={uid}
              className="w-full justify-center"
              // If you later want to trigger onFollow, wrap FollowButton with your own handler or expose a callback prop in FollowButton
            />
          </div>
        </div>
      )}
    </div>
  );
}

